import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";

import { Button } from "components/atoms/button/index";
import { CloseButton } from "components/atoms/signup-closebutton/index";
import { ReactComponent as TickIcon } from "assets/images/tick.svg";
import { ReactComponent as ExclamationIcon } from "assets/images/exclamation.svg";
import { EmailField, PasswordField, SigninResponseMessage } from "./constants";
import { Header } from "components/atoms/header/index";
import { UserService } from "core/service/user.service";
import { AuthService } from "core/service/auth.service";
import { Config } from "core/service/config.service";
import { Field } from "components/molecules/Field";
import { UtilService } from "core/utils/utils";
import { BsEyeFill, BsEyeSlashFill } from "react-icons/bs";
import { Loader } from "components/atoms/loader";
import { cocoAffiliate } from "core/utils/constants";
import * as S from "./signin.styles";

export const SignIn = () => {
  const { t } = useTranslation();
  const [showText, setShowText] = useState(false);
  const [affiliate, setAffiliate] = useState(false);
  const [refsite, setRefsite] = useState(false);
  const [showSignup, setShowSignup] = useState(true);
  const [cocoUser, setCocoUser] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ mode: "onBlur" });

  const [PopupResponse, setPopupResponse] = useState("");
  const [responseMessage, setResponseMessage] = useState("");

  useEffect(() => {
    if (!PopupResponse) return;
    setResponseMessage(
      PopupResponse === "error"
        ? SigninResponseMessage.error
        : SigninResponseMessage.success
    );
    if (PopupResponse === "success") {
      const timer = setTimeout(() => {
        setPopupResponse("");
      }, 8000);

      return () => clearTimeout(timer);
    }
  }, [PopupResponse]);
  // eslint-disable-next-line
  useEffect(() => {
    const affiliate = AuthService.getAffiliateDetails();
    const refsite = UtilService.getCookie("gst_ref_test");
    setCocoUser(
      UtilService.getCookie("coconut_user")
        ? UtilService.getCookie("coconut_user")
        : ""
    );
    setAffiliate(affiliate);
    setRefsite(refsite);
    invalidCoconutRedirect();
    AuthService.removeAllCookies();
  }, []);

  const invalidCoconutRedirect = () => {
    /* Check if session invalid and redirect back to coconut */
    const coconutUser = UtilService.getCookie("coconut_user");
    const redirectDomain = UtilService.getCookie("redirect_domain");
    const redirectUrls = {
      direct: `${Config.getCoconutDirectUrl(redirectDomain)}/self-assessment`,
      advisor: `${Config.getCoconutAccountantUrl(
        redirectDomain
      )}/client/${UtilService.getCookie(
        "coconut_business_id"
      )}/self-assessment`,
    };
    const redirectToCoconut = redirectUrls[coconutUser];
    if (redirectToCoconut) {
      setShowSignup(!showSignup);
      window.location.href = redirectToCoconut;
    }
  };

  const onSubmit = (values) => {
    let request = values;
    request.affiliate = affiliate.aff_id;
    values.refsite = refsite;
    setPopupResponse("loading");
    UserService.signin(request).then((res) => {
      if (res && Object.keys(res.payload).length) {
        setPopupResponse("success");
        setTimeout(() => {
          onSigninSuccess(res);
        }, 1500);
      } else {
        setPopupResponse("error");
      }
    });
  };

  const onSigninSuccess = (values) => {
    AuthService.setSessionid(values.payload.sessionid);
    redirectToSimpletaxEdit();
  };

  const redirectToSimpletaxEdit = () => {
    const redirectUrl = Config.getRedirectUrl();
    const confirmationKey = window.location.href.split("/")[6];
    const regexPattern = new RegExp("confirm-email");
    regexPattern.test(window.location.href)
      ? (window.location = `${redirectUrl}confirm-email/${confirmationKey}`)
      : (window.location = `${redirectUrl}`);
  };

  return (
    <div>
      <Header company={cocoUser ? cocoAffiliate : "simpletax"} />
      {showSignup ? (
        <S.Container>
          <S.CardHeader
            color={affiliate.background ? affiliate.background : ""}
          >
            {t("signIn.header")}
          </S.CardHeader>
          <S.CardBody>
            {PopupResponse === "loading" && <Loader />}
            {["success", "error"].includes(PopupResponse) && (
              <S.Overlay>
                <S.Response type={PopupResponse}>
                  <S.ResponseContent>
                    <S.AlertIcon state={PopupResponse}>
                      {PopupResponse === "error" ? (
                        <ExclamationIcon />
                      ) : (
                        <TickIcon />
                      )}
                    </S.AlertIcon>
                    <S.ResponseMessage>{responseMessage}</S.ResponseMessage>
                    <CloseButton
                      state={PopupResponse}
                      onClick={() => setPopupResponse("")}
                    ></CloseButton>
                  </S.ResponseContent>
                </S.Response>
              </S.Overlay>
            )}
            <S.Form onSubmit={handleSubmit(onSubmit)}>
              {EmailField.map((obj, index) => (
                <Field
                  labelcolor="light"
                  background="grey"
                  {...obj}
                  register={register}
                  error={errors[obj.name]?.message}
                  key={index}
                />
              ))}
              {PasswordField.map((obj, index) => (
                <div key={index}>
                  <Field
                    labelcolor="light"
                    type={UtilService.setPasswordType(PasswordField, showText)}
                    background="grey"
                    {...obj}
                    register={register}
                    error={errors[obj.name]?.message}
                    key={index}
                  />
                  <S.EyeContainer>
                    <S.Eye onClick={() => setShowText(!showText)}>
                      {showText ? <BsEyeFill /> : <BsEyeSlashFill />}
                    </S.Eye>
                  </S.EyeContainer>
                </div>
              ))}
              <S.ButtonContainer>
                <Button
                  value={t("signIn.label.signin")}
                  size="120"
                  height="42"
                  background={affiliate.background ? affiliate.background : ""}
                />
              </S.ButtonContainer>
            </S.Form>
          </S.CardBody>
          <S.RegisterWrap>
            <Link to="/forgot-password">
              {t("signIn.label.forgot_password")}
            </Link>
            <Link to="/signup">{t("signIn.label.register_message")}</Link>
            <Link to="/resend-activation">{t("activationCode.linkTitle")}</Link>
          </S.RegisterWrap>
        </S.Container>
      ) : (
        <Loader />
      )}
    </div>
  );
};
